<template>
  <v-card
    class="ors-chart"
    :class="`ors-chart--type-${chartType}`"
  >
    <v-card-title class="ors-chart-title">
      {{ title }}
    </v-card-title>
    <v-card-text>
      <div
        ref="chartdiv"
        class="ors-chart-chartdiv"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import am4lang from '@amcharts/amcharts4/lang/pt_BR';
import buildOrsChartTheme from '../lib/buildOrsChartTheme';

export default {
  props: {
    title: {
      type: String,
      required: true,
    },
    chartType: {
      type: Number,
      required: true,
    },
    chartData: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    chart: null,
    chartTheme: null,
    lastOpenTooltipData: null,
  }),
  computed: {
    chartBuiltData() {
      return this.chartData.map(d => ({
        date: new Date(Number(d.encerradoEm)),
        value: Number(d.tempo),
        event: d.evento,
        inPlace: new Date(Number(d.data)),
        by: d.atendidoPor,
        info: d.informacao,
        img: d.foto,
      }));
    },
  },
  watch: {
    async chartType() {
      this.loadThemes();
      // wait any other prop changes
      await this.$nextTick();
      // rebuild
      this.destroyChart();
      this.buildChart();
    },
  },
  mounted() {
    this.loadThemes();
    this.buildChart();
  },
  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    loadThemes() {
      am4core.unuseAllThemes();
      am4core.useTheme(am4themesAnimated);
      am4core.useTheme(buildOrsChartTheme(this.chartType));
    },
    destroyChart() {
      this.chart.dispose();
    },
    buildChart() {
      const chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart);

      chart.language.locale = am4lang;

      chart.paddingRight = 20;

      chart.data = this.chartBuiltData;

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      dateAxis.renderer.minGridDistance = 40;
      dateAxis.startLocation = 0;
      dateAxis.extraMin = 0.1;
      dateAxis.extraMax = 0.1;
      dateAxis.endLocation = 1;
      dateAxis.title.fontWeight = '500';
      dateAxis.title.text = 'Período da ocorrência';

      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.tooltip.disabled = true;
      valueAxis.renderer.minWidth = 35;
      valueAxis.title.text = 'tempo (min)';

      const tooltipHTML = `<dl class="ors-chart-chartdiv-tp">
        <dt>Evento: </dt>
        <dd>{event}</dd>
        <dt>Ocorrido em:</dt>
        <dd>{dateX}</dd>
        <dt>No local em:</dt>
        <dd>{inPlace}</dd>
        <dt>Tempo:</dt>
        <dd>{valueY.value} min</dd>
      </dl>`;

      const bullet = new am4charts.CircleBullet();

      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = 'value';
      series.dataFields.dateX = 'date';
      series.dataFields.event = 'event';
      series.dataFields.inPlace = 'inPlace';
      series.strokeWidth = 1.5;
      series.tensionX = 1;
      series.bullets.push(bullet);
      series.connect = true;
      // series.tooltipText = '{valueY.value}';
      series.tooltipHTML = tooltipHTML;
      series.events.on('tooltipshownat', ({ dataItem }) => {
        this.lastOpenTooltipData = dataItem.dataContext;
      });
      series.tooltip.events.on('hidden', () => {
        this.lastOpenTooltipData = null;
      });

      chart.cursor = new am4charts.XYCursor();

      chart.events.on('hit', ({ target }) => {
        if (!this.lastOpenTooltipData) return;

        const { zoomOutButton } = target;
        if (zoomOutButton ? zoomOutButton.isHidden : true) {
          this.$emit('open-modal', this.lastOpenTooltipData);
        }
      });

      this.chart = chart;
    },
  },
};
</script>

<style lang="stylus" scoped>
@import '~@/style/variables.css';
.ors-chart {
  width: 100%;
  height: 250px;
  border-left: solid 5px;

  .ors-chart-title {
    border-bottom: 1px solid;
    margin: 0 30px 20px;
    padding: 8px 0;
    padding-bottom: 4px;
    color: inherit;
    font-size: 13px;
    font-weight: bold;
  }

  /deep/ .v-card__text {
    font-size: .75em;
    font-weight: 500;
    color: var(--other-color-1);
  }

  &.ors-chart--type-2 {
    color: var(--dash-color-1);
    border-color: var(--dash-color-1);
  }
  &.ors-chart--type-1 {
    color: var(--dash-color-2);
    border-color: var(--dash-color-2);
  }
  &.ors-chart--type-3 {
    color: var(--dash-color-3);
    border-color: var(--dash-color-3);
  }

  /deep/ .ors-chart-chartdiv-tp {
    font-size: .9em;
    display: grid;
    grid-template-columns: auto auto;
    grid-column-gap: 1em;

    dd {
      color: #404040;
    }
  }
}
</style>
